/**
 * @file Valeurs de la messagerie entre un client et l'établissement autour d'une réservation.
 * @module common.constants.restaurants.reservationMessages
 *
 * Miroir du fichier backend
 * backend/src/modules/restaurants/constants/reservationMessages.js : les
 * auteurs, filtres, motifs et codes d'erreur sont échangés avec l'API et ne
 * doivent jamais être renommés. Le serveur reste l'autorité sur la longueur.
 */

/** Auteur d'un message. */
export const MESSAGE_AUTHORS = Object.freeze({
  CUSTOMER: 'customer',
  RESTAURANT: 'restaurant',
});

/**
 * Messagerie ouverte par l'établissement (désactivée par défaut).
 *
 * Seule lecture de ce réglage côté écrans : le menu, la page Messages,
 * l'onglet de la fiche réservation et les pastilles du carnet doivent
 * toujours être d'accord. Le serveur applique la même règle.
 *
 * @param {Object|null|undefined} restaurantConfig - Configuration du restaurant
 * @return {boolean} Vrai si la messagerie est activée
 */
export const isMessagingEnabled = (restaurantConfig) => restaurantConfig?.messagingSettings?.enabled === true;

/**
 * Date du dernier message affiché, envoyée avec une réponse : le serveur ne
 * marque lus que les messages jusqu'à elle, pas ceux arrivés pendant la frappe.
 *
 * @param {Array<Object>} messages - Messages du fil, du plus ancien au plus récent
 * @return {string|null} Date du dernier message, ou null sur un fil vide
 */
export const getSeenUntil = (messages) => messages[messages.length - 1]?.createdAt || null;

/** Longueur maximale d'un message, contrôlée aussi par le serveur. */
export const MESSAGE_BODY_MAX_LENGTH = 500;

/** Filtres de la liste des discussions du dashboard. */
export const CONVERSATION_LIST_FILTERS = Object.freeze({
  ALL: 'all',
  UNREAD: 'unread',
  FAVORITES: 'favorites',
});

/** Actions appliquées d'un coup aux discussions sélectionnées dans la liste. */
export const CONVERSATION_BULK_ACTIONS = Object.freeze({
  MARK_READ: 'mark_read',
  MARK_UNREAD: 'mark_unread',
  DELETE: 'delete',
});

/** Motifs pour lesquels le client ne peut plus écrire. */
export const CUSTOMER_WRITE_BLOCK_REASONS = Object.freeze({
  RESERVATION_CANCELLED: 'reservation_cancelled',
  WINDOW_CLOSED: 'window_closed',
  ANSWERED: 'answered',
});

/**
 * Façon dont le client échange avec l'établissement : discussion libre, ou
 * question unique (le client écrit jusqu'à la réponse de l'établissement).
 */
export const MESSAGING_MODES = Object.freeze({
  CONVERSATION: 'conversation',
  SINGLE_QUESTION: 'single_question',
});

/** Mode d'un établissement qui n'a rien choisi. */
export const DEFAULT_MESSAGING_MODE = MESSAGING_MODES.CONVERSATION;

/** Codes d'erreur renvoyés par l'API sous `errorType`. */
export const MESSAGE_ERROR_CODES = Object.freeze({
  MESSAGING_DISABLED: 'messaging_disabled',
  CUSTOMER_CANNOT_WRITE: 'customer_cannot_write',
  CUSTOMER_WITHOUT_EMAIL: 'customer_without_email',
  INVALID_BODY: 'invalid_message_body',
});

/**
 * Ancre de la discussion sur la page de gestion du client : le lien de
 * l'email de réponse se termine par `#messages`.
 */
export const MESSAGES_ANCHOR_ID = 'messages';

/**
 * Rafraîchissement d'une discussion ouverte : il n'y a pas de temps réel, un
 * nouveau message apparaît au plus tard après ce délai.
 */
export const MESSAGES_REFRESH_INTERVAL_MS = 20000;

/** Rafraîchissement du compteur de non lus (menu, carnet), aligné sur la cloche. */
export const UNREAD_SUMMARY_REFRESH_INTERVAL_MS = 30000;
